import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ColorInput.scss";

/** The swatches offered first: the brand, then a spread of clear colours. */
const PRESETS = [
  "#ff5536",
  "#2c42c3",
  "#f1c40f",
  "#0e1116",
  "#1f7a3d",
  "#c62828",
  "#e67e22",
  "#16a085",
  "#2980b9",
  "#8e44ad",
  "#d81b60",
  "#6d4c41",
  "#00897b",
  "#3949ab",
  "#7cb342",
  "#5c5f66",
];

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export type ColorInputProps = {
  /** "#rrggbb" */
  value: string;
  /** Gets { target: { value, name } } like a native input's onChange */
  onChange: (event: { target: { value: string; name?: string } }) => void;
  name?: string;
  id?: string;
  disabled?: boolean;
  /** Extra swatches to offer first (e.g. the colours already used) */
  presets?: string[];
  className?: string;
  "aria-label"?: string;
};

/** The brand colour picker (never the browser's): a swatch + hex field that opens swatches and a hex input. */
export function ColorInput({ value, onChange, name, id, disabled = false, presets, className, "aria-label": ariaLabel }: ColorInputProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [pos, setPos] = useState<React.CSSProperties>({ visibility: "hidden" });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => setDraft(value), [value]);

  const emit = (v: string) => onChange({ target: { value: v.toLowerCase(), name } });

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !popRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const h = popRef.current.offsetHeight;
    const w = popRef.current.offsetWidth;
    const below = window.innerHeight - r.bottom - 8 >= h;
    setPos({ left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)), ...(below ? { top: r.bottom + 4 } : { bottom: window.innerHeight - r.top + 4 }) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const swatches = Array.from(new Set([...(presets ?? []), ...PRESETS].map((c) => c.toLowerCase())));
  const valid = HEX.test(value);

  return (
    <span className={["ui-color", className].filter(Boolean).join(" ")}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className="ui-color__field"
        disabled={disabled}
        aria-label={ariaLabel ?? "Choose a colour"}
        aria-expanded={open}
        onClick={() => {
          setPos({ visibility: "hidden" });
          setOpen((o) => !o);
        }}
      >
        <span className="ui-color__chip" style={{ background: valid ? value : "transparent" }} />
        <span className="ui-color__hex">{value || "—"}</span>
      </button>
      {open &&
        createPortal(
          <div ref={popRef} className="ui-color__pop" style={pos} role="dialog" aria-label="Colour">
            <div className="ui-color__grid">
              {swatches.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`ui-color__swatch${c === value.toLowerCase() ? " is-on" : ""}`}
                  style={{ background: c }}
                  aria-label={c}
                  data-tooltip={c}
                  onClick={() => {
                    emit(c);
                    setOpen(false);
                    triggerRef.current?.focus();
                  }}
                />
              ))}
            </div>
            <label className="ui-color__custom">
              <span className="ui-color__chip ui-color__chip--lg" style={{ background: HEX.test(draft) ? draft : "transparent" }} />
              <input
                className="ui-input ui-input--sm"
                value={draft}
                maxLength={7}
                spellCheck={false}
                aria-label="Hex colour"
                onChange={(e) => {
                  const v = e.target.value.startsWith("#") ? e.target.value : `#${e.target.value}`;
                  setDraft(v);
                  if (HEX.test(v)) emit(v);
                }}
              />
            </label>
          </div>,
          document.body
        )}
    </span>
  );
}

export default ColorInput;
